import { OutlineChevronRightIcon } from '@tempmee/react-icons';
import { Avatar, Column, Row, TableCell, TableRow, Typography, Button } from '@tempmee/react';
import { useJobProfessionDisplay } from '~/hooks/useJobProfessionDisplay';
import { ProfessionTypeEnum } from '~/models';

export interface IBidNomineeRowProps {
  /** Nominee's display name — the Professional column's primary line. */
  name: string;
  /**
   * Shift code from the pair's most recent shift ("RDH", "DA", …). Rendered as its
   * full display name ("Dental Hygienist") beneath the nominee's name.
   */
  profession?: string;
  /** Office that worked the shift — the Office column. */
  officeName?: string;
  /** Nominee's profile photo. Omit to fall back to the DS default tooth avatar. */
  avatarUrl?: string;
  /** CTA copy under the "Complete Survey" column. @default 'Complete' */
  ctaLabel?: string;
  /** Disables the CTA (e.g. while another nominee's survey dialog is open). @default false */
  disabled?: boolean;
  /** Fired when the CTA is pressed. */
  onComplete: () => void;
  'data-cy'?: string;
}

/**
 * One Best in Dental nominee as a row of the nominees table: photo avatar with
 * the nominee's name and profession, the office that worked the shift, and the
 * survey CTA.
 *
 * Presentational only — all data arrives via props and the CTA surfaces as
 * `onComplete`; the tab owns the cycle/gate/dialog wiring. Must be rendered
 * inside {@link BidNomineeTable}, which supplies the `<table>` and its headers.
 */
export const BidNomineeRow = ({
  name,
  profession,
  officeName,
  avatarUrl,
  ctaLabel = 'Complete',
  disabled = false,
  onComplete,
  'data-cy': dataCy,
}: IBidNomineeRowProps) => {
  // Reuses the shared code→display-name mapping (handles sub-professions too),
  // falling back to the raw code if the server ever sends an unmapped one.
  const professionName =
    useJobProfessionDisplay({ profession: profession as ProfessionTypeEnum }) || profession;

  return (
    <TableRow hover data-cy={dataCy}>
      <TableCell>
        <Row align="center" gap={4} className="min-w-0">
          <Avatar size="xl" src={avatarUrl} alt="" />

          <Column className="min-w-0 gap-0.5">
            <Typography variant="body1" as="span" bold numberOfLines={1}>
              {name}
            </Typography>
            {professionName && (
              <Typography
                variant="body1"
                as="span"
                numberOfLines={1}
                className="text-text-secondary"
              >
                {professionName}
              </Typography>
            )}
          </Column>
        </Row>
      </TableCell>

      <TableCell>
        <Typography variant="body1" as="span" className="text-text-secondary">
          {officeName}
        </Typography>
      </TableCell>

      <TableCell>
        <Button
          variant="text"
          color="primary"
          className="shrink-0"
          data-cy="bid-complete-survey-button"
          disabled={disabled}
          endIcon={<OutlineChevronRightIcon size={16} />}
          onClick={onComplete}
        >
          {ctaLabel}
        </Button>
      </TableCell>
    </TableRow>
  );
};
